/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql, type TypedDocumentNode } from '@apollo/client'
import { type QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ConfirmDialog, ErrorAlert, PaginationControl, SearchExpand } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import {
  Badge,
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSections,
  DropdownMenuTrigger,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@repo/shared-client/components/ui'
import { Form, useForm, useWatch } from '@repo/shared-client/form'
import { createFileRoute, stripSearchParams, useSearch } from '@tanstack/react-router'
import SFProfileBanner from '@web/src/components/primatives/sf-profile-banner/sf-profile-banner'
import ChatGroupMemberLimitNotice from '@web/src/features/chat/chat-group-member-limit-notice/chat-group-member-limit-notice'
import { ChatMemberMuteMenuItems } from '@web/src/features/chat/chat-member-mute-menu/chat-member-mute-menu'
import ChatMembersHeader from '@web/src/features/chat/chat-members-header/chat-members-header'
import {
  BAN_CHAT_MEMBER,
  REMOVE_CHAT_MEMBER,
  RESTRICT_CHAT_MEMBER,
  SET_CHAT_MEMBER_ROLE,
  TRANSFER_CHAT_ROOM_OWNERSHIP,
  UNBAN_CHAT_MEMBER,
  UNRESTRICT_CHAT_MEMBER,
} from '@web/src/features/chat/chat-queries'
import { restrictChatMemberUntilIso } from '@web/src/features/chat/chat-utils'
import { useChatListSearchNavigation } from '@web/src/features/chat/hooks/use-chat-list-search-navigation'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { ProfileBasicInfo, ProfileMediaInfo } from '@web/src/lib/fragments'
import i18n from '@web/src/lib/intl/i18n'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import type { TFunction } from 'i18next'
import {
  ArrowLeftRightIcon,
  BanIcon,
  EllipsisIcon,
  ShieldIcon,
  UserMinusIcon,
  UserSearchIcon,
  UsersIcon,
} from 'lucide-react'
import { Fragment, useCallback, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import type {
  ChatMemberModerationStatus,
  ChatMemberRole,
  ChatMemberSortEnum,
  ChatMembersPageQuery,
  ChatMembersPageQueryVariables,
  ChatMembersPageRoomQuery,
  ChatMembersPageRoomQueryVariables,
} from 'types/graphql'
import { z } from 'zod'

dayjs.extend(relativeTime)

const MUTE_HOURS_DISPLAY_THRESHOLD = 24

function muteRestrictionLabel(mutedUntil: string | null | undefined, t: TFunction) {
  if (!mutedUntil) {
    return t('Muted')
  }

  const until = dayjs(mutedUntil)
  const hoursRemaining = until.diff(dayjs(), 'hour', true)

  if (hoursRemaining < MUTE_HOURS_DISPLAY_THRESHOLD) {
    const hours = Math.max(1, Math.ceil(hoursRemaining))
    if (hours === 1) {
      return t('Muted for 1 hour')
    }
    return t('Muted for {{count}} hours', { count: hours })
  }

  return t('Muted until {{time}}', { time: until.format('MMM D') })
}

export const CHAT_MEMBERS_PAGE_ROOM_QUERY: TypedDocumentNode<
  ChatMembersPageRoomQuery,
  ChatMembersPageRoomQueryVariables
> = gql`
  query ChatMembersPageRoomQuery($id: String!) {
    chatRoom(id: $id) {
      id
      name
      type
      memberCount
      archivedAt
      canInvite
      canModerate
      otherMember {
        id
        profile {
          ...ProfileBasicInfo
        }
      }
    }
  }
  ${ProfileBasicInfo}
  ${ProfileMediaInfo}
`

export const CHAT_MEMBERS_PAGE_QUERY: TypedDocumentNode<ChatMembersPageQuery, ChatMembersPageQueryVariables> = gql`
  query ChatMembersPageQuery($page: Int, $filter: ChatMembersPageFilter!, $sortBy: ChatMemberSortEnum) {
    chatMembersPage(page: $page, filter: $filter, sortBy: $sortBy) {
      entries {
        id
        createdAt
        chatRoomId
        profileId
        role
        mutedUntil
        mutedBy {
          ...ProfileBasicInfo
        }
        isRestricted
        bannedAt
        bannedBy {
          ...ProfileBasicInfo
        }
        isBanned
        moderationStatus
        canChangeRole
        canRemove
        canRestrict
        canBan
        canUnban
        canTransferOwnership
        profile {
          ...ProfileBasicInfo
        }
      }
      count
      perPage
    }
  }
  ${ProfileBasicInfo}
  ${ProfileMediaInfo}
`

type MembersSearchInput = {
  page?: number
  name?: string
  role?: string
  status?: string
  sortBy?: string
}

type MembersLoaderDeps = {
  page: number
  name?: string
  role?: string
  status?: string
  sortBy: string
}

function resolveMembersSearchParams(search: MembersSearchInput): MembersLoaderDeps {
  return {
    page: search.page ?? 1,
    name: search.name,
    role: search.role,
    status: search.status,
    sortBy: search.sortBy ?? 'ROLE',
  }
}

function membersQueryVariables(roomId: string, deps: MembersLoaderDeps): ChatMembersPageQueryVariables {
  return {
    page: deps.page,
    filter: {
      chatRoomId: roomId,
      name: deps.name,
      role: deps.role as ChatMemberRole | undefined,
      moderationStatus: deps.status as ChatMemberModerationStatus | undefined,
    },
    sortBy: deps.sortBy as ChatMemberSortEnum,
  }
}

type MemberEntry = NonNullable<ChatMembersPageQuery['chatMembersPage']>['entries'][number]

function memberRoleLabel(role: string, t: TFunction) {
  if (role === 'OWNER') {
    return t('Owner')
  }
  if (role === 'ADMIN') {
    return t('Admin')
  }
  return null
}

function ChatMemberRow({
  canModerate,
  member,
  readOnly,
  onAction,
  onOpenProfile,
}: {
  canModerate: boolean
  member: MemberEntry
  readOnly?: boolean
  onAction: (member: MemberEntry, action: string, muteHours?: number) => void
  onOpenProfile: (profileId: string) => void
}) {
  const { t } = useTranslation()
  const profile = member.profile
  if (!profile) {
    return null
  }

  const label = memberRoleLabel(member.role, t)
  const canManageRoles = member.canChangeRole
  const canRemove = member.canRemove
  const canRestrict = Boolean(member.canRestrict && !readOnly)
  const canBan = Boolean(member.canBan && !readOnly)
  const canUnban = Boolean(member.canUnban && !readOnly)
  const canTransferToMember = member.canTransferOwnership
  const canModerateMember = canRestrict || canBan || canUnban
  const showActions = canManageRoles || canRemove || canTransferToMember || canModerateMember
  const showBannedBadge = canModerate && member.isBanned
  const showMutedBadge = canModerate && member.isRestricted && !member.isBanned

  return (
    <Card
      className="w-full overflow-hidden border-border bg-muted/50 py-0 transition-colors hover:border-ring hover:bg-muted data-[size=sm]:py-1"
      size="sm"
    >
      <CardContent className="px-3 py-1">
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center">
            <SFProfileBanner
              className="cursor-pointer"
              lowerTitle={
                <div className="flex flex-col items-start gap-0">
                  <div className="flex flex-wrap items-center gap-1">
                    {label ? (
                      <Badge size="sm" variant="secondary">
                        {label}
                      </Badge>
                    ) : null}
                    {showBannedBadge ? (
                      <Badge size="sm" variant="destructive">
                        {t('Banned')}
                      </Badge>
                    ) : null}
                    {showMutedBadge ? (
                      <Badge className="bg-amber-500 text-black" size="sm">
                        {muteRestrictionLabel(member.mutedUntil, t)}
                      </Badge>
                    ) : null}
                  </div>
                  <p className="line-clamp-1 text-muted-foreground text-xs">
                    {t('Joined: {{time}}', { time: dayjs(member.createdAt).fromNow() })}
                  </p>
                  {showBannedBadge && member.bannedBy?.name ? (
                    <p className="line-clamp-1 text-muted-foreground text-xs">
                      {t('Banned by: {{name}}', { name: member.bannedBy.name })}
                    </p>
                  ) : null}
                  {showMutedBadge && member.mutedBy?.name ? (
                    <p className="line-clamp-1 text-muted-foreground text-xs">
                      {t('Muted by: {{name}}', { name: member.mutedBy.name })}
                    </p>
                  ) : null}
                </div>
              }
              onClick={() => onOpenProfile(profile.id)}
              profile={profile}
              size="xl"
            />
          </div>

          {showActions ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    aria-label={t('Member options')}
                    className="shrink-0"
                    size="icon"
                    type="button"
                    variant="ghost"
                  >
                    <EllipsisIcon aria-hidden />
                  </Button>
                }
              />
              <DropdownMenuContent align="end" side="bottom" sideOffset={4}>
                <DropdownMenuSections>
                  {canManageRoles || canTransferToMember ? (
                    <Fragment key="manage">
                      {canManageRoles ? (
                        <DropdownMenuItem
                          onClick={() =>
                            onAction(member, member.role === 'ADMIN' ? 'set-member-role' : 'set-admin-role')
                          }
                        >
                          <ShieldIcon aria-hidden className="size-4" />
                          {member.role === 'ADMIN' ? t('Remove admin') : t('Make admin')}
                        </DropdownMenuItem>
                      ) : null}
                      {canTransferToMember ? (
                        <DropdownMenuItem onClick={() => onAction(member, 'transfer')}>
                          <ArrowLeftRightIcon aria-hidden className="size-4" />
                          {t('Transfer ownership')}
                        </DropdownMenuItem>
                      ) : null}
                    </Fragment>
                  ) : null}
                  {(canRestrict && !member.isBanned) || canUnban ? (
                    <Fragment key="moderation">
                      {canRestrict && !member.isBanned ? (
                        <ChatMemberMuteMenuItems
                          isRestricted={member.isRestricted}
                          onMute={(hours) => onAction(member, 'mute', hours)}
                          onUnmute={() => onAction(member, 'unmute')}
                        />
                      ) : null}
                      {canUnban ? (
                        <DropdownMenuItem onClick={() => onAction(member, 'unban')}>
                          <UsersIcon aria-hidden className="size-4" />
                          {t('Unban')}
                        </DropdownMenuItem>
                      ) : null}
                    </Fragment>
                  ) : null}
                  {canBan || canRemove ? (
                    <Fragment key="destructive">
                      {canBan ? (
                        <DropdownMenuItem onClick={() => onAction(member, 'ban')} variant="destructive">
                          <BanIcon aria-hidden className="size-4" />
                          {t('Ban from conversation')}
                        </DropdownMenuItem>
                      ) : null}
                      {canRemove ? (
                        <DropdownMenuItem onClick={() => onAction(member, 'remove')} variant="destructive">
                          <UserMinusIcon aria-hidden className="size-4" />
                          {t('Remove from conversation')}
                        </DropdownMenuItem>
                      ) : null}
                    </Fragment>
                  ) : null}
                </DropdownMenuSections>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}

export const Route = createFileRoute('/(auth)/(main-layout)/chat/$roomId_/members')({
  component: ChatMembersPage,
  validateSearch: z.object({
    page: z.coerce.number().optional(),
    name: z.string().optional(),
    role: z.string().optional(),
    status: z.string().optional(),
    sortBy: z.string().optional(),
  }),
  search: {
    middlewares: [
      stripSearchParams({
        page: 1,
        name: '',
        role: '',
        status: '',
        sortBy: 'ROLE',
      }),
    ],
  },
  head: () => {
    const t = i18n.t.bind(i18n)
    return seo({
      title: `${t('Members')} | ${env.VITE_APP_NAME}`,
      description: t('Browse conversation members.'),
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    })
  },
  loaderDeps: ({ search }) => resolveMembersSearchParams(search),
  loader: async ({ context, deps, params }) => {
    const resolvedDeps = deps as MembersLoaderDeps
    const variables = membersQueryVariables(params.roomId, resolvedDeps)

    const [{ queryRef: roomQueryRef }, { queryRef: membersQueryRef }] = await Promise.all([
      queryAndPreload(context, CHAT_MEMBERS_PAGE_ROOM_QUERY, {
        variables: { id: params.roomId },
      }),
      queryAndPreload(context, CHAT_MEMBERS_PAGE_QUERY, { variables }),
    ])

    return { roomQueryRef, membersQueryRef }
  },
})

function ChatMembersPage() {
  const { t } = useTranslation()
  const search = useSearch({ strict: false })
  const resolvedSearch = useMemo(() => resolveMembersSearchParams(search), [search])
  const { navigate, isLoading, handlePageChange, handleSearchChange } = useChatListSearchNavigation(search)
  const formRef = useRef(null)
  const [removeModal, setRemoveModal] = useState(false)
  const [memberToRemove, setMemberToRemove] = useState<MemberEntry | null>(null)
  const [banModal, setBanModal] = useState(false)
  const [memberToBan, setMemberToBan] = useState<MemberEntry | null>(null)
  const [transferModal, setTransferModal] = useState(false)
  const [memberToTransfer, setMemberToTransfer] = useState<MemberEntry | null>(null)
  const client = useApolloClient()

  const { roomQueryRef, membersQueryRef } = Route.useLoaderData()
  const { data: roomData, error: roomError } = useReadQuery(roomQueryRef as QueryRef<ChatMembersPageRoomQuery>)
  const { data, error } = useReadQuery(membersQueryRef as QueryRef<ChatMembersPageQuery>)
  const room = roomData?.chatRoom

  const formDefaultValues = useMemo(
    () => ({
      page: resolvedSearch.page,
      name: resolvedSearch.name ?? '',
      role: resolvedSearch.role as ChatMemberRole | undefined,
      status: resolvedSearch.status as ChatMemberModerationStatus | undefined,
      sortBy: resolvedSearch.sortBy,
    }),
    [resolvedSearch.page, resolvedSearch.name, resolvedSearch.role, resolvedSearch.status, resolvedSearch.sortBy]
  )

  const formMethods = useForm({
    defaultValues: formDefaultValues,
  })

  const [setMemberRole] = useMutation(SET_CHAT_MEMBER_ROLE, {
    onCompleted: () => {
      toast.success(t('Member updated successfully'))
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
    },
  })

  const [removeMember] = useMutation(REMOVE_CHAT_MEMBER, {
    onCompleted: async () => {
      toast.success(t('Member removed successfully'))
      await client.refetchQueries({ include: [CHAT_MEMBERS_PAGE_QUERY, CHAT_MEMBERS_PAGE_ROOM_QUERY] })
      setRemoveModal(false)
      setMemberToRemove(null)
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
      setRemoveModal(false)
      setMemberToRemove(null)
    },
  })

  const [restrictMember] = useMutation(RESTRICT_CHAT_MEMBER, {
    onCompleted: async () => {
      toast.success(t('Member muted successfully'))
      await client.refetchQueries({ include: [CHAT_MEMBERS_PAGE_QUERY] })
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
    },
  })

  const [unrestrictMember] = useMutation(UNRESTRICT_CHAT_MEMBER, {
    onCompleted: async () => {
      toast.success(t('Member unmuted successfully'))
      await client.refetchQueries({ include: [CHAT_MEMBERS_PAGE_QUERY] })
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
    },
  })

  const [banMember] = useMutation(BAN_CHAT_MEMBER, {
    onCompleted: async () => {
      toast.success(t('Member banned successfully'))
      await client.refetchQueries({ include: [CHAT_MEMBERS_PAGE_QUERY, CHAT_MEMBERS_PAGE_ROOM_QUERY] })
      setBanModal(false)
      setMemberToBan(null)
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
      setBanModal(false)
      setMemberToBan(null)
    },
  })

  const [unbanMember] = useMutation(UNBAN_CHAT_MEMBER, {
    onCompleted: async () => {
      toast.success(t('Member unbanned successfully'))
      await client.refetchQueries({ include: [CHAT_MEMBERS_PAGE_QUERY, CHAT_MEMBERS_PAGE_ROOM_QUERY] })
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
    },
  })

  const [transferOwnership] = useMutation(TRANSFER_CHAT_ROOM_OWNERSHIP, {
    onCompleted: async () => {
      toast.success(t('Ownership transferred successfully'))
      await client.refetchQueries({ include: [CHAT_MEMBERS_PAGE_QUERY, CHAT_MEMBERS_PAGE_ROOM_QUERY] })
      setTransferModal(false)
      setMemberToTransfer(null)
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
      setTransferModal(false)
      setMemberToTransfer(null)
    },
  })

  const filterResults = useCallback(() => {
    const curPage = formMethods.getValues('page')
    const name = formMethods.getValues('name')
    const role = formMethods.getValues('role') as ChatMemberRole | undefined
    const status = formMethods.getValues('status') as ChatMemberModerationStatus | undefined
    const sortBy = formMethods.getValues('sortBy') as ChatMemberSortEnum

    navigate({
      search: {
        page: curPage,
        name: name || undefined,
        sortBy,
        role: role || undefined,
        status: status || undefined,
      },
      replace: true,
      viewTransition: false,
    } as never)
  }, [formMethods, navigate])

  const handleLiveFilter = useCallback(() => {
    filterResults()
  }, [filterResults])

  const handleResetFilters = () => {
    formMethods.reset({
      page: 1,
      name: '',
      role: undefined,
      status: undefined,
      sortBy: 'ROLE',
    })

    navigate({
      search: {
        page: 1,
        name: undefined,
        role: undefined,
        status: undefined,
        sortBy: 'ROLE',
      },
      replace: true,
      viewTransition: false,
    } as never)
  }

  const handleAction = (member: MemberEntry, action: string, muteHours?: number) => {
    if (
      room?.archivedAt &&
      (action === 'ban' ||
        action === 'unban' ||
        action === 'mute' ||
        action === 'unmute' ||
        action === 'remove' ||
        action === 'transfer' ||
        action === 'set-admin-role' ||
        action === 'set-member-role')
    ) {
      return
    }

    if (action === 'remove') {
      setMemberToRemove(member)
      setRemoveModal(true)
      return
    }

    if (action === 'ban') {
      setMemberToBan(member)
      setBanModal(true)
      return
    }

    if (action === 'unban') {
      unbanMember({
        variables: {
          chatRoomId: member.chatRoomId,
          profileId: member.profileId,
        },
      })
      return
    }

    if (action === 'mute' && muteHours != null) {
      restrictMember({
        variables: {
          chatRoomId: member.chatRoomId,
          profileId: member.profileId,
          until: restrictChatMemberUntilIso(muteHours),
        },
      })
      return
    }

    if (action === 'unmute') {
      unrestrictMember({
        variables: {
          chatRoomId: member.chatRoomId,
          profileId: member.profileId,
        },
      })
      return
    }

    if (action === 'transfer') {
      setMemberToTransfer(member)
      setTransferModal(true)
      return
    }

    if (action === 'set-admin-role' || action === 'set-member-role') {
      setMemberRole({
        variables: {
          chatRoomId: member.chatRoomId,
          profileId: member.profileId,
          role: action === 'set-admin-role' ? 'ADMIN' : 'MEMBER',
        },
      })
    }
  }

  const pageData = useMemo(
    () => data?.chatMembersPage || { entries: [], count: 0, perPage: 50 },
    [data?.chatMembersPage]
  )
  const currentPage = useWatch({ control: formMethods.control, name: 'page' })
  const watchName = useWatch({ control: formMethods.control, name: 'name' })

  if (error || roomError) {
    return <ErrorAlert error={error || roomError} />
  }

  if (!room) {
    return (
      <div className="mx-auto w-full max-w-4xl p-2">
        <p className="py-16 text-center text-muted-foreground">{t('This conversation is not available.')}</p>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-4xl p-2">
      <div className="flex w-full flex-col items-stretch gap-4">
        <ChatMembersHeader activeTab="members" room={room} />
        <ChatGroupMemberLimitNotice room={room} />

        <Form className="w-full" formMethods={formMethods} ref={formRef}>
          <div className="flex w-full flex-col items-stretch gap-4">
            <SearchExpand
              clearSearchLabel={t('Clear search')}
              inputProps={{ 'aria-label': t('Filter members') }}
              loading={isLoading}
              minChars={0}
              name="name"
              onSearchChange={handleSearchChange}
              placeholder={t('Filter members by name...')}
              toggleFiltersLabel={t('Toggle filters')}
            >
              <Card className="gap-0 py-0">
                <CardContent className="px-2 py-3">
                  <div className="flex w-full flex-col gap-4">
                    <div className="flex w-full flex-wrap items-start gap-2">
                      <NativeSelectFormField label={t('Roles')} name="role" onValueChange={handleLiveFilter}>
                        <option value="">{t('All Roles')}</option>
                        <option value="OWNER">{t('Owners')}</option>
                        <option value="ADMIN">{t('Admins')}</option>
                        <option value="MEMBER">{t('Members')}</option>
                      </NativeSelectFormField>

                      {room.canModerate ? (
                        <NativeSelectFormField label={t('Status')} name="status" onValueChange={handleLiveFilter}>
                          <option value="">{t('All Status')}</option>
                          <option value="ACTIVE">{t('Active')}</option>
                          <option value="RESTRICTED">{t('Muted')}</option>
                          <option value="BANNED">{t('Banned')}</option>
                        </NativeSelectFormField>
                      ) : null}

                      <NativeSelectFormField label={t('Sort By')} name="sortBy" onValueChange={handleLiveFilter}>
                        <option value="ROLE">{t('Role')}</option>
                        <option value="NAME">{t('Name')}</option>
                        <option value="NEWEST">{t('Newest')}</option>
                        <option value="OLDEST">{t('Oldest')}</option>
                      </NativeSelectFormField>
                    </div>

                    <div className="flex w-full justify-end">
                      <Button onClick={handleResetFilters} type="button" variant="secondary">
                        {t('Reset')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </SearchExpand>

            {pageData.entries.length > 0 ? (
              <div className="flex w-full flex-col gap-4">
                <div className="flex w-full flex-col gap-1">
                  {pageData.entries.map((member) => (
                    <ChatMemberRow
                      canModerate={room.canModerate}
                      key={member.id}
                      member={member}
                      onAction={handleAction}
                      onOpenProfile={(profileId) =>
                        navigate({
                          to: '/profile/$profileId/about',
                          params: { profileId },
                        } as never)
                      }
                      readOnly={Boolean(room.archivedAt)}
                    />
                  ))}
                </div>
                <PaginationControl
                  count={pageData.count}
                  onPageChange={handlePageChange}
                  page={currentPage}
                  pageSize={pageData.perPage}
                />
              </div>
            ) : null}

            {pageData.entries.length === 0 ? (
              <Empty className="py-20">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <UserSearchIcon />
                  </EmptyMedia>
                  <EmptyTitle>{t('No profiles found')}</EmptyTitle>
                  {watchName ? <EmptyDescription>{t('Try adjusting your search or filters.')}</EmptyDescription> : null}
                </EmptyHeader>
              </Empty>
            ) : null}
          </div>
        </Form>
      </div>

      <ConfirmDialog
        cancelText={t('No')}
        confirmText={t('Yes')}
        onConfirm={() => {
          if (memberToRemove) {
            removeMember({
              variables: {
                chatRoomId: memberToRemove.chatRoomId,
                profileId: memberToRemove.profileId,
              },
            })
          }
        }}
        onOpenChange={(details) => {
          setRemoveModal(details.open)
          if (!details.open) {
            setMemberToRemove(null)
          }
        }}
        open={removeModal}
        text={t('Are you sure you want to remove {{name}} from the conversation?', {
          name: memberToRemove?.profile?.name,
        })}
        title={t('Remove Member')}
        variant="delete"
      />

      <ConfirmDialog
        cancelText={t('No')}
        confirmText={t('Yes')}
        onConfirm={() => {
          if (memberToBan) {
            banMember({
              variables: {
                chatRoomId: memberToBan.chatRoomId,
                profileId: memberToBan.profileId,
              },
            })
          }
        }}
        onOpenChange={(details) => {
          setBanModal(details.open)
          if (!details.open) {
            setMemberToBan(null)
          }
        }}
        open={banModal}
        text={t(
          'Are you sure you want to ban {{name}} from this conversation? They will not be able to read messages or rejoin until they are unbanned.',
          { name: memberToBan?.profile?.name }
        )}
        title={t('Ban Member')}
        variant="delete"
      />

      <ConfirmDialog
        cancelText={t('Cancel')}
        confirmationPlaceholder={t('Type confirm')}
        confirmationPrompt={t("Enter 'confirm' in the box below")}
        confirmationText="confirm"
        confirmText={t('Transfer ownership')}
        onConfirm={() => {
          if (memberToTransfer) {
            transferOwnership({
              variables: {
                chatRoomId: memberToTransfer.chatRoomId,
                newOwnerId: memberToTransfer.profileId,
              },
            })
          }
        }}
        onOpenChange={(details) => {
          setTransferModal(details.open)
          if (!details.open) {
            setMemberToTransfer(null)
          }
        }}
        open={transferModal}
        text={t(
          'Are you sure you want to transfer ownership to {{name}}? They will have full control of this conversation.',
          { name: memberToTransfer?.profile?.name }
        )}
        title={t('Transfer ownership')}
        variant="delete"
      />
    </div>
  )
}
